import { ErrorMessage } from '@hookform/error-message';
import { isValidCodePostal, isValidDate, isValidDateTime, isValidPhoneNumber, isValidTime } from '@workspace/utils';
import { Controller, FieldValues, get, Path, RegisterOptions, useFormContext } from 'react-hook-form';
import { RenderedContent } from '../../utils';
import {
  DateInput,
  DateTimeInput,
  Description,
  Field,
  IbanInput,
  Input,
  Label,
  LabelProps,
  PhoneFormatHint,
  PhoneInput,
  PostalCodeInput,
  SirenInput,
  TextError,
  TimeInput,
} from '../form';

export interface FieldPatternInputProps<TFormValues extends Record<string, never> = any> extends LabelProps {
  label?: RenderedContent;
  name: Path<TFormValues> | string;
  validation?: RegisterOptions<FieldValues, string>;
  description?: RenderedContent;
  type: 'iban' | 'date' | 'datetime' | 'siren' | 'time' | 'postalCode' | 'phone';
  loading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: string;
}

const INPUT_TYPES = {
  iban: IbanInput,
  date: DateInput,
  datetime: DateTimeInput,
  time: TimeInput,
  siren: SirenInput,
  postalCode: PostalCodeInput,
  phone: PhoneInput,
} as const;

const VALIDATORS = {
  date: { isValidDate },
  datetime: { isValidDateTime },
  time: { isValidTime },
  phone: { isValidPhoneNumber },
  postalCode: { isValidCodePostal },
} as const;

const VALUE_TRANSFORMERS = {
  date: (values: any) => values.formattedValue,
  datetime: (values: any) => values.formattedValue,
  time: (values: any) => values.formattedValue,
  siren: (values: any) => values.floatValue,
  default: (values: any) => values.value,
} as const;

/**
 * Input avec formatage automatique selon le type
 * - IBAN, date, téléphone, SIREN, code postal
 * - Validation intégrée selon le format
 * - Transformation automatique des valeurs
 */
export function FieldPatternInput({
  type,
  label,
  name,
  required = true,
  children,
  validation = {},
  disabled,
  description,
  className,
  defaultValue,
  ...props
}: FieldPatternInputProps) {
  const methods = useFormContext();

  const FormattedInput = INPUT_TYPES[type] as any;

  // Ajouter les validateurs spécifiques au type
  if (type in VALIDATORS) {
    validation.validate = {
      ...validation.validate,
      ...VALIDATORS[type as keyof typeof VALIDATORS],
    };
  }

  const transformValue = (values: any) => {
    const transformer = VALUE_TRANSFORMERS[type as keyof typeof VALUE_TRANSFORMERS] || VALUE_TRANSFORMERS.default;
    return transformer(values);
  };

  return (
    <Field className={className}>
      <Label required={required}>{label}</Label>
      <Controller
        name={name}
        control={methods.control}
        defaultValue={defaultValue}
        render={({ field: { onChange, ...field } }) => (
          <FormattedInput
            {...field}
            {...props}
            onValueChange={(values: any) => onChange(transformValue(values))}
            disabled={methods.formState.isSubmitting || disabled}
            error={get(methods.formState.errors, name)}
            customInput={Input}
          />
        )}
        rules={{
          required: {
            value: required,
            message: 'Ce champs est requis',
          },
          ...validation,
        }}
      />
      {type === 'phone' && !disabled && <PhoneFormatHint />}
      <ErrorMessage
        errors={methods.formState.errors}
        name={name}
        render={({ message }) => <TextError>{message}</TextError>}
      />
      <Description>{description}</Description>
      {children}
    </Field>
  );
}
